/**
 * CanvasLogger - v2 Taxonomy Wrapper for canvas/rendering logging
 *
 * Domain: canvas
 * Taxonomy: canvas.{operation}[.{flavor}]
 *
 * Operations:
 * - frame: Frame rendering and timing
 * - render: General render operations and cycles
 * - scroll: Scroll events and positioning
 * - viewport: Viewport resize and zoom
 * - line: Line rendering and positioning
 * - page: Page rendering and visibility
 * - selection: Selection rendering and highlighting
 * - cursor: Cursor/caret rendering
 * - glyph: Glyph rendering and caching
 * - layout: Layout calculations and reflow
 * - pipeline: Render pipeline state
 * - font: Font loading and resolution
 * - lifecycle: Component mount/unmount
 * - pageSetup: Page setup and pagination config
 * - wasm: WASM worker operations (canvas-specific)
 * - document: Document init and pagination
 * - snapshot: Snapshot and cache operations
 * - edit: Edit command execution
 * - dom: DOM diagnostics
 *
 * Flavors: .start, .complete, .fail, .perf, .diag, .gap, .hit, .miss, .skip
 *
 * All methods capture full stack traces via captureCallerSource().
 *
 * @example
 * CanvasLogger.frame.perf(16.7, { lineCount: 100 });
 * CanvasLogger.line.render.perf(50, { count: 100 });
 * CanvasLogger.glyph.render.perf(100, 5.2);
 */

import { WeBelieveLogger, captureCallerSource } from '../WeBelieveLogger';

export const CanvasLogger = {
  // ============================================================================
  // FRAME OPERATIONS - canvas.frame.*
  // ============================================================================
  frame: {
    /**
     * Log frame render performance
     * Component: canvas.frame.perf
     */
    perf: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('canvas.frame.perf', 'Frame rendered', durationMs, source, data);
    },
  },

  // ============================================================================
  // RENDER OPERATIONS - canvas.render.*
  // ============================================================================
  render: {
    /**
     * Log a general render operation
     * Component: canvas.render.perf
     */
    perf: (element: string, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('canvas.render.perf', `Rendered ${element}`, durationMs, source, data);
    },

    /**
     * Log render cycle begin
     * Component: canvas.render.start
     */
    start: (cycleId: number, pageIndex: number, reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.render.start', `Render cycle ${cycleId} begin (page ${pageIndex}): ${reason}`, source, { cycleId, pageIndex, reason, ...data });
    },

    /**
     * Log render cycle complete
     * Component: canvas.render.complete
     */
    complete: (cycleId: number, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('canvas.render.complete', `Render cycle ${cycleId} complete`, durationMs, source, { cycleId, ...data });
    },

    /**
     * Log render skipped
     * Component: canvas.render.skip
     */
    skip: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.render.skip', `Render skipped: ${reason}`, source, data);
    },

    /**
     * Log render throttled
     * Component: canvas.render.throttle
     */
    throttle: (timeSinceLastMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.render.throttle', `Render throttled (${timeSinceLastMs}ms since last)`, source, data);
    },

    /**
     * Log incremental render
     * Component: canvas.render.incremental
     */
    incremental: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.render.incremental', `Incremental render (page ${pageIndex})`, source, data);
    },
  },

  // ============================================================================
  // SCROLL OPERATIONS - canvas.scroll.*
  // ============================================================================
  scroll: {
    /**
     * Log a scroll event
     * Component: canvas.scroll
     */
    to: (position: number, behavior: 'instant' | 'smooth', data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.scroll', `Scroll to ${position} (${behavior})`, source, { position, behavior, ...data });
    },
  },

  // ============================================================================
  // VIEWPORT OPERATIONS - canvas.viewport.*
  // ============================================================================
  viewport: {
    /**
     * Log viewport resize
     * Component: canvas.viewport.resize
     */
    resize: (width: number, height: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.viewport.resize', `Viewport resized: ${width}x${height}`, source, { width, height, ...data });
    },

    /**
     * Log viewport zoom
     * Component: canvas.viewport.zoom
     */
    zoom: (scale: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.viewport.zoom', `Zoom: ${scale}x`, source, { scale, ...data });
    },
  },

  // ============================================================================
  // LINE OPERATIONS - canvas.line.*
  // ============================================================================
  line: {
    /**
     * Log line render performance
     * Component: canvas.line.render.perf
     */
    render: {
      perf: (count: number, durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.line.render.perf', `Rendered ${count} lines`, durationMs, source, { count, ...data });
      },
    },

    /**
     * Log visible lines
     * Component: canvas.line.visible
     */
    visible: (range: { start: number; end: number }, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.line.visible', `Visible lines: ${range.start}-${range.end}`, source, range);
    },

    /**
     * Log line y_positions for debugging layout collapse (coalesce-to-line-0 bug)
     * Component: canvas.line.positions.diag
     */
    positions: {
      diag: (pageIndex: number, lines: Array<{start: number; end: number; y: number}>, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        // Detect y_position collision (multiple lines at same y = bug indicator)
        const yPositions = lines.map(l => l.y);
        const uniqueYs = new Set(yPositions);
        const hasCollision = uniqueYs.size < lines.length && lines.length > 1;
        const allZero = lines.length > 0 && lines.every(l => l.y === 0);

        // Build compact line summary: "[start-end)@y"
        const lineSummary = lines.slice(0, 10).map(l => `[${l.start}-${l.end})@${l.y.toFixed(0)}`).join(', ');
        const truncated = lines.length > 10 ? `... +${lines.length - 10} more` : '';

        WeBelieveLogger.logWithSource(
          hasCollision || allZero ? 'warn' : 'debug',
          'canvas.line.positions.diag',
          `Page ${pageIndex} line positions: ${lines.length} lines`,
          source,
          {
            pageIndex,
            lineCount: lines.length,
            uniqueYCount: uniqueYs.size,
            hasCollision,
            allZero,
            lineSummary: lineSummary + truncated,
            yPositions: lines.slice(0, 20).map(l => l.y),
            ...data
          }
        );
      },
    },

    /**
     * Log y-position collision (bug indicator)
     * Component: canvas.line.gap.collision
     */
    gap: {
      collision: (pageIndex: number, collisionCount: number, collidingYs: number[], data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource(
          'error',
          'canvas.line.gap.collision',
          `Y-COLLISION DETECTED: Page ${pageIndex} has ${collisionCount} lines at same y_position`,
          source,
          {
            pageIndex,
            collisionCount,
            collidingYs: collidingYs.slice(0, 10),
            bugIndicator: 'COALESCE_TO_LINE_0',
            ...data
          }
        );
      },

      /**
       * Log paragraph ID mismatch in OLD format
       * Component: canvas.line.gap.idMismatch
       */
      idMismatch: (pageIndex: number, unmatchedCount: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource(
          'error',
          'canvas.line.gap.idMismatch',
          `Page ${pageIndex}: ID MISMATCH - ${unmatchedCount} slices have no matching cached snapshot`,
          source,
          { pageIndex, unmatchedCount, bugIndicator: 'PARAGRAPH_ID_MISMATCH', ...data }
        );
      },

      /**
       * Log slice extraction produces 0 lines
       * Component: canvas.line.gap.emptySlice
       */
      emptySlice: (pageIndex: number, sliceInfo: Record<string, unknown>, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource(
          'warn',
          'canvas.line.gap.emptySlice',
          `Page ${pageIndex}: Slice produced 0 lines`,
          source,
          { pageIndex, ...sliceInfo, bugIndicator: 'EMPTY_SLICE', ...data }
        );
      },
    },

    /**
     * Log stale lines filtered
     * Component: canvas.line.stale
     */
    stale: {
      filtered: (pageIndex: number, staleCount: number, committedLength: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource(
          staleCount > 0 ? 'warn' : 'debug',
          'canvas.line.stale',
          `Page ${pageIndex}: Filtered ${staleCount} stale lines (committedLen=${committedLength})`,
          source,
          { pageIndex, staleCount, committedLength, ...data }
        );
      },
    },

    /**
     * Log OLD slice format fallback
     * Component: canvas.line.format.old
     */
    format: {
      old: (pageIndex: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource(
          'warn',
          'canvas.line.format.old',
          `Page ${pageIndex}: Using OLD slice format (fallback) - may indicate stale WASM`,
          source,
          { pageIndex, format: 'old', bugIndicator: 'STALE_WASM_FORMAT', ...data }
        );
      },

      /**
       * Log slice extraction summary (OLD format)
       * Component: canvas.line.format.extraction
       */
      extraction: (pageIndex: number, lineCount: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource(
          lineCount === 0 ? 'warn' : 'debug',
          'canvas.line.format.extraction',
          `Page ${pageIndex}: Slice extraction complete - ${lineCount} lines`,
          source,
          { pageIndex, lineCount, format: 'old', ...data }
        );
      },
    },
  },

  // ============================================================================
  // PAGE OPERATIONS - canvas.page.*
  // ============================================================================
  page: {
    /**
     * Log page render performance
     * Component: canvas.page.render.perf
     */
    render: {
      perf: (pageIndex: number, durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.page.render.perf', `Rendered page ${pageIndex}`, durationMs, source, { pageIndex, ...data });
      },
    },

    /**
     * Log visible pages
     * Component: canvas.page.visible
     */
    visible: (range: { start: number; end: number }, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.page.visible', `Visible pages: ${range.start}-${range.end}`, source, range);
    },

    /**
     * Log page setup changed
     * Component: canvas.page.setup.changed
     */
    setup: {
      changed: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'canvas.page.setup.changed', 'Page setup changed', source, data);
      },

      /**
       * Log page cache invalidated
       * Component: canvas.page.setup.invalidate
       */
      invalidate: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'canvas.page.setup.invalidate', 'Page cache invalidated', source, data);
      },

      /**
       * Log force reinit flag set
       * Component: canvas.page.setup.forceReinit
       */
      forceReinit: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'canvas.page.setup.forceReinit', 'Force reinit flag set', source, data);
      },
    },
  },

  // ============================================================================
  // SELECTION OPERATIONS - canvas.selection.*
  // ============================================================================
  selection: {
    /**
     * Log selection rendered
     * Component: canvas.selection.render
     */
    render: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.selection.render', 'Selection rendered', source, { durationMs, ...data });
    },

    /**
     * Log selection cleared
     * Component: canvas.selection.clear
     */
    clear: () => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.selection.clear', 'Selection cleared', source);
    },

    /**
     * Log selection highlight
     * Component: canvas.selection.highlight
     */
    highlight: (rangeCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.selection.highlight', `Highlighted ${rangeCount} ranges`, source, { rangeCount, ...data });
    },
  },

  // ============================================================================
  // CURSOR OPERATIONS - canvas.cursor.*
  // ============================================================================
  cursor: {
    /**
     * Log cursor rendered
     * Component: canvas.cursor.render
     */
    render: (position: { x: number; y: number }, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.cursor.render', `Cursor at (${position.x}, ${position.y})`, source, position);
    },

    /**
     * Log cursor blink
     * Component: canvas.cursor.blink
     */
    blink: (visible: boolean) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.cursor.blink', `Cursor ${visible ? 'visible' : 'hidden'}`, source);
    },
  },

  // ============================================================================
  // GLYPH OPERATIONS - canvas.glyph.*
  // ============================================================================
  glyph: {
    /**
     * Log glyph render performance
     * Component: canvas.glyph.render.perf
     */
    render: {
      perf: (glyphCount: number, durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.glyph.render.perf', `Rendered ${glyphCount} glyphs`, durationMs, source, { glyphCount, ...data });
      },
    },

    /**
     * Log glyph warm
     * Component: canvas.glyph.warm
     */
    warm: (fontFamily: string, glyphCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.warm', `Warmed ${glyphCount} glyphs for ${fontFamily}`, source, { fontFamily, glyphCount, ...data });
    },

    /**
     * Log glyph cache miss
     * Component: canvas.glyph.miss
     */
    miss: (glyph: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.miss', `Glyph cache miss: "${glyph}"`, source, data);
    },

    /**
     * Log glyph path received from worker
     * Component: canvas.glyph.received
     */
    received: (fontUid: string, sizePx: number, gid: number, bufferSize: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.received', `Received glyph path for ${fontUid}@${sizePx}:${gid}`, source, { fontUid, sizePx, gid, bufferSize, ...data });
    },

    /**
     * Log glyph path decoded
     * Component: canvas.glyph.decoded
     */
    decoded: (gid: number, operationCount: number, isEmpty: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.decoded', `Decoded ${operationCount} operations for glyph ${gid} (${isEmpty ? 'EMPTY' : 'non-empty'})`, source, { gid, operationCount, isEmpty, ...data });
    },

    /**
     * Log glyph cached
     * Component: canvas.glyph.cached
     */
    cached: (cacheKey: string, operationCount: number | 'empty', data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const desc = operationCount === 'empty' ? 'EMPTY sentinel' : `${operationCount} operations`;
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.cached', `Cached glyph for ${cacheKey} (${desc})`, source, { cacheKey, operationCount, ...data });
    },

    /**
     * Log glyph re-render triggered
     * Component: canvas.glyph.rerender
     */
    rerender: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.rerender', 'Glyph cached, triggering incremental re-render', source, data);
    },

    /**
     * Log Path2D build from operations
     * Component: canvas.glyph.path2d
     */
    path2d: (totalOps: number, validOps: number, invalidOps: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.path2d', `buildPath2DFromOps: Processed ${totalOps} operations (${validOps} valid, ${invalidOps} invalid)`, source, { totalOps, validOps, invalidOps, ...data });
    },

    /**
     * Log font analysis for glyph warming diagnostics
     * Component: canvas.glyph.fontAnalysis
     *
     * Used to track which fonts are available in cached snapshots vs render snapshots,
     * helping diagnose issues where bold/italic text doesn't render on initial load.
     */
    fontAnalysis: (snapshotCount: number, totalLines: number, fontsFound: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.glyph.fontAnalysis', `Font analysis: ${snapshotCount} snapshots, ${totalLines} lines, fonts: [${fontsFound.join(', ')}]`, source, { snapshotCount, totalLines, fontsFound, ...data });
    },
  },

  // ============================================================================
  // LAYOUT OPERATIONS - canvas.layout.*
  // ============================================================================
  layout: {
    /**
     * Log layout calculation performance
     * Component: canvas.layout.calculate.perf
     */
    calculate: {
      perf: (durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.layout.calculate.perf', 'Layout calculated', durationMs, source, data);
      },
    },

    /**
     * Log layout invalidated
     * Component: canvas.layout.invalidate
     */
    invalidate: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.layout.invalidate', `Layout invalidated: ${reason}`, source, data);
    },

    /**
     * Log layout reflow performance
     * Component: canvas.layout.reflow.perf
     */
    reflow: {
      perf: (pageCount: number, durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.layout.reflow.perf', `Reflow ${pageCount} pages`, durationMs, source, { pageCount, ...data });
      },
    },
  },

  // ============================================================================
  // PIPELINE OPERATIONS - canvas.pipeline.*
  // ============================================================================
  pipeline: {
    /**
     * Log pipeline started
     * Component: canvas.pipeline.start
     */
    start: (operation: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.pipeline.start', `Pipeline started: ${operation}`, source, data);
    },

    /**
     * Log pipeline complete
     * Component: canvas.pipeline.complete
     */
    complete: (operation: string, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('canvas.pipeline.complete', `Pipeline complete: ${operation}`, durationMs, source, data);
    },

    /**
     * Log pipeline error
     * Component: canvas.pipeline.fail
     */
    fail: (operation: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'canvas.pipeline.fail', `Pipeline error in ${operation}: ${error}`, source, data);
    },

    /** @deprecated Use pipeline.fail() */
    error: (operation: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'canvas.pipeline.fail', `Pipeline error in ${operation}: ${error}`, source, data);
    },
  },

  // ============================================================================
  // FONT OPERATIONS - canvas.font.*
  // ============================================================================
  font: {
    /**
     * Log font load performance
     * Component: canvas.font.load.perf
     */
    load: {
      perf: (fontFamily: string, durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.font.load.perf', `Font loaded: ${fontFamily}`, durationMs, source, data);
      },
    },

    /**
     * Log font fallback
     * Component: canvas.font.fallback
     */
    fallback: (requested: string, fallback: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'canvas.font.fallback', `Font fallback: ${requested} → ${fallback}`, source, data);
    },

    /**
     * Log font ready
     * Component: canvas.font.ready
     */
    ready: (fontFamily: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.font.ready', `Font ready: ${fontFamily}`, source, data);
    },

    /**
     * Log font resolved
     * Component: canvas.font.resolved
     */
    resolved: (fontFamily: string, fontUid: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.font.resolved', `Font resolved: ${fontFamily} → ${fontUid}`, source, data);
    },
  },

  // ============================================================================
  // LIFECYCLE OPERATIONS - canvas.lifecycle.*
  // ============================================================================
  lifecycle: {
    /**
     * Log component mount
     * Component: canvas.lifecycle.mount
     */
    mount: (componentName: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.lifecycle.mount', `${componentName} mounted`, source, data);
    },

    /**
     * Log component unmount
     * Component: canvas.lifecycle.unmount
     */
    unmount: (componentName: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.lifecycle.unmount', `${componentName} unmounted`, source, data);
    },

    /**
     * Log callback/handler registered
     * Component: canvas.lifecycle.registered
     */
    registered: (handlerName: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.lifecycle.registered', `${handlerName} registered`, source, data);
    },
  },

  // ============================================================================
  // STATE OPERATIONS - canvas.state.*
  // ============================================================================
  state: {
    /**
     * Log stale prop update ignored
     * Component: canvas.state.stale
     */
    stale: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.state.stale', `Stale update ignored: ${reason}`, source, data);
    },

    /**
     * Log state sync skipped (duplicate/no-op)
     * Component: canvas.state.skip
     */
    skip: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.state.skip', `State sync skipped: ${reason}`, source, data);
    },
  },

  // ============================================================================
  // WASM OPERATIONS (canvas-specific) - canvas.wasm.*
  // ============================================================================
  wasm: {
    /**
     * Log WASM worker init
     * Component: canvas.wasm.init.start
     */
    init: {
      start: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'canvas.wasm.init.start', 'Starting typesetter initialization', source, data);
      },

      /**
       * Log init config
       * Component: canvas.wasm.init.config
       */
      config: (skipInitialization: boolean, sharedWasmClient: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'canvas.wasm.init.config', `Init config: skipInitialization=${skipInitialization}, sharedWasmClient=${sharedWasmClient}`, source, {
          skipInitialization,
          sharedWasmClient,
          ...data
        });
      },
    },

    /**
     * Log WASM worker ready
     * Component: canvas.wasm.ready
     */
    ready: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.wasm.ready', 'WASM worker ready', source, data);
    },

    /**
     * Log WASM worker error
     * Component: canvas.wasm.fail
     */
    fail: (error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'canvas.wasm.fail', `WASM worker error: ${error}`, source, data);
    },

    /**
     * Log WASM worker cleanup
     * Component: canvas.wasm.cleanup
     */
    cleanup: {
      /**
       * Log cleanup complete
       * Component: canvas.wasm.cleanup.complete
       */
      complete: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'canvas.wasm.cleanup.complete', 'WASM worker cleanup', source, data);
      },

      /**
       * Log cleanup skipped
       * Component: canvas.wasm.cleanup.skip
       */
      skip: (reason: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'canvas.wasm.cleanup.skip', `Cleanup: Skipping worker termination (${reason})`, source, {
          reason,
          ...data
        });
      },
    },

    /**
     * Log shared worker operations
     * Component: canvas.wasm.shared.*
     */
    shared: {
      using: (phase?: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'canvas.wasm.shared.using', `Using shared WASM worker${phase ? ` (${phase})` : ''}`, source, {
          phase,
          ...data
        });
      },

      ready: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'canvas.wasm.shared.ready', 'Shared worker setup complete', source, data);
      },
    },

    // Legacy method aliases for backward compatibility
    /** @deprecated Use wasm.init.start() */
    initStart: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.wasm.init.start', 'Starting typesetter initialization', source, data);
    },

    /** @deprecated Use wasm.init.config() */
    initConfig: (skipInitialization: boolean, sharedWasmClient: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.wasm.init.config', `Init config: skipInitialization=${skipInitialization}, sharedWasmClient=${sharedWasmClient}`, source, {
        skipInitialization,
        sharedWasmClient,
        ...data
      });
    },

    /** @deprecated Use wasm.shared.using() */
    usingSharedWorker: (phase?: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.wasm.shared.using', `Using shared WASM worker${phase ? ` (${phase})` : ''}`, source, {
        phase,
        ...data
      });
    },

    /** @deprecated Use wasm.shared.ready() */
    sharedWorkerReady: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.wasm.shared.ready', 'Shared worker setup complete', source, data);
    },

    /** @deprecated Use wasm.fail() */
    error: (error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'canvas.wasm.fail', `WASM worker error: ${error}`, source, data);
    },

    /** @deprecated Use wasm.cleanup.skip() */
    cleanupSkipped: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.wasm.cleanup.skip', `Cleanup: Skipping worker termination (${reason})`, source, { reason, ...data });
    },
  },

  // ============================================================================
  // DOCUMENT OPERATIONS - canvas.document.*
  // ============================================================================
  document: {
    /**
     * Log document init
     * Component: canvas.document.init
     */
    init: (textLength: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.document.init', `Document initializing (${textLength} chars)`, source, { textLength, ...data });
    },

    /**
     * Log document paginate performance
     * Component: canvas.document.paginate.perf
     */
    paginate: {
      perf: (pageCount: number, durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('canvas.document.paginate.perf', `Paginated into ${pageCount} pages`, durationMs, source, { pageCount, ...data });
      },
    },

    /**
     * Log paragraphs init
     * Component: canvas.document.paragraphs.init
     */
    paragraphs: {
      init: (count: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'canvas.document.paragraphs.init', `Initializing ${count} paragraphs`, source, { count, ...data });
      },
    },

    /**
     * Log empty to non-empty transition
     * Component: canvas.document.transition
     */
    transition: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.document.transition', 'Empty → non-empty transition detected', source, data);
    },

    /** @deprecated Use document.paragraphs.init() */
    paragraphsInit: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.document.paragraphs.init', `Initializing ${count} paragraphs`, source, { count, ...data });
    },

    /** @deprecated Use document.transition() */
    emptyToNonEmpty: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.document.transition', 'Empty → non-empty transition detected', source, data);
    },
  },

  // ============================================================================
  // SNAPSHOT OPERATIONS - canvas.snapshot.*
  // ============================================================================
  snapshot: {
    /**
     * Log snapshot hit (cached)
     * Component: canvas.snapshot.hit
     */
    hit: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.snapshot.hit', `Got ${count} cached snapshots`, source, { count, ...data });
    },

    /**
     * Log snapshot miss
     * Component: canvas.snapshot.miss
     */
    miss: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'canvas.snapshot.miss', 'No cached snapshots available', source, data);
    },

    /**
     * Log snapshot cached (alias for hit)
     * @deprecated Use snapshot.hit()
     */
    cached: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.snapshot.hit', `Got ${count} cached snapshots`, source, { count, ...data });
    },

    /**
     * Log snapshot missing (alias for miss)
     * @deprecated Use snapshot.miss()
     */
    missing: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'canvas.snapshot.miss', 'No cached snapshots available', source, data);
    },
  },

  // ============================================================================
  // EDIT OPERATIONS - canvas.edit.*
  // ============================================================================
  edit: {
    /**
     * Log edit command execute
     * Component: canvas.edit.execute
     */
    execute: (command: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.edit.execute', `Execute command: ${command}`, source, data);
    },

    /**
     * Log edit command success
     * Component: canvas.edit.complete
     */
    complete: (command: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.edit.complete', `Command success: ${command}`, source, data);
    },

    /**
     * Log edit command failed
     * Component: canvas.edit.fail
     */
    fail: (command: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'canvas.edit.fail', `Command failed: ${command} - ${error}`, source, data);
    },

    /**
     * Log typesetter not available
     * Component: canvas.edit.gap.unavailable
     */
    gap: {
      unavailable: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'canvas.edit.gap.unavailable', 'TypesetterWorkerClient not available', source, data);
      },
    },
  },

  // ============================================================================
  // DOM DIAGNOSTICS - canvas.dom.*
  // ============================================================================
  dom: {
    /**
     * Log DOM diagnostic info
     * Component: canvas.dom.diag
     */
    diag: (pageIndex: number, canvasCount: number, canvasSizes: string[], inputCount: number, editableCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.dom.diag', `page=${pageIndex} canvases=${canvasCount} canvasSizes=[${canvasSizes.join(',')}] inputs=${inputCount} editables=${editableCount}`, source, {
        pageIndex,
        canvasCount,
        canvasSizes,
        inputCount,
        editableCount,
        ...data
      });
    },
  },

  // ============================================================================
  // CLICK OPERATIONS - canvas.click.*
  // ============================================================================
  click: {
    /**
     * Log stale snapshot detection on click
     * Component: canvas.click.staleSnapshot
     */
    staleSnapshot: (pageIndex: number, localStartGrapheme: number, authoritativeStartGrapheme: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'canvas.click.staleSnapshot', `Page ${pageIndex}: Stale snapshot detected (local=${localStartGrapheme}, authoritative=${authoritativeStartGrapheme})`, source, {
        pageIndex,
        localStartGrapheme,
        authoritativeStartGrapheme,
        ...data
      });
    },

    /**
     * Log snapshot refresh on click
     * Component: canvas.click.snapshotRefreshed
     */
    snapshotRefreshed: (pageIndex: number, lineCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'canvas.click.snapshotRefreshed', `Page ${pageIndex}: Refreshed snapshot with ${lineCount} lines`, source, {
        pageIndex,
        lineCount,
        ...data
      });
    },
  },

  // ============================================================================
  // LEGACY COMPATIBILITY - Maps old method signatures to v2 taxonomy
  // These maintain backward compatibility while emitting v2 component names
  // ============================================================================

  /** @deprecated Use CanvasLogger.frame.perf() */
  legacyFrame: (durationMs: number, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.performanceWithSource('canvas.frame.perf', 'Frame rendered', durationMs, source, data);
  },

  /** @deprecated Use CanvasLogger.render.perf() */
  legacyRender: (element: string, durationMs: number, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.performanceWithSource('canvas.render.perf', `Rendered ${element}`, durationMs, source, data);
  },

  /** @deprecated Use CanvasLogger.scroll.to() */
  legacyScroll: (position: number, behavior: 'instant' | 'smooth', data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'canvas.scroll', `Scroll to ${position} (${behavior})`, source, { position, behavior, ...data });
  },

  // Legacy nested objects (for backward compatibility)
  lines: {
    /** @deprecated Use CanvasLogger.line.render.perf() */
    render: (count: number, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('canvas.line.render.perf', `Rendered ${count} lines`, durationMs, source, { count, ...data });
    },
    /** @deprecated Use CanvasLogger.line.visible() */
    visible: (range: { start: number; end: number }, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'canvas.line.visible', `Visible lines: ${range.start}-${range.end}`, source, range);
    },
    /** @deprecated Use CanvasLogger.line.positions.diag() */
    positions: (pageIndex: number, lines: Array<{start: number; end: number; y: number}>, data?: Record<string, unknown>) => {
      CanvasLogger.line.positions.diag(pageIndex, lines, data);
    },
    /** @deprecated Use CanvasLogger.line.gap.collision() */
    yCollision: (pageIndex: number, collisionCount: number, collidingYs: number[], data?: Record<string, unknown>) => {
      CanvasLogger.line.gap.collision(pageIndex, collisionCount, collidingYs, data);
    },
    /** @deprecated Use CanvasLogger.line.stale.filtered() */
    staleFiltered: (pageIndex: number, staleCount: number, committedLength: number, data?: Record<string, unknown>) => {
      CanvasLogger.line.stale.filtered(pageIndex, staleCount, committedLength, data);
    },
    /** @deprecated Use CanvasLogger.line.format.old() */
    oldFormatFallback: (pageIndex: number, data?: Record<string, unknown>) => {
      CanvasLogger.line.format.old(pageIndex, data);
    },
    /** @deprecated Use CanvasLogger.line.gap.idMismatch() */
    idMismatch: (pageIndex: number, unmatchedCount: number, data?: Record<string, unknown>) => {
      CanvasLogger.line.gap.idMismatch(pageIndex, unmatchedCount, data);
    },
    /** @deprecated Use CanvasLogger.line.gap.emptySlice() */
    sliceEmpty: (pageIndex: number, sliceInfo: Record<string, unknown>, data?: Record<string, unknown>) => {
      CanvasLogger.line.gap.emptySlice(pageIndex, sliceInfo, data);
    },
    /** @deprecated Use CanvasLogger.line.format.extraction() */
    sliceExtraction: (pageIndex: number, lineCount: number, data?: Record<string, unknown>) => {
      CanvasLogger.line.format.extraction(pageIndex, lineCount, data);
    },
  },

  pages: {
    /** @deprecated Use CanvasLogger.page.render.perf() */
    render: (pageIndex: number, durationMs: number, data?: Record<string, unknown>) => {
      CanvasLogger.page.render.perf(pageIndex, durationMs, data);
    },
    /** @deprecated Use CanvasLogger.page.visible() */
    visible: (range: { start: number; end: number }, data?: Record<string, unknown>) => {
      CanvasLogger.page.visible(range, data);
    },
  },

  renderCycle: {
    /** @deprecated Use CanvasLogger.render.start() */
    begin: (cycleId: number, pageIndex: number, reason: string, data?: Record<string, unknown>) => {
      CanvasLogger.render.start(cycleId, pageIndex, reason, data);
    },
    /** @deprecated Use CanvasLogger.render.complete() */
    end: (cycleId: number, durationMs: number, data?: Record<string, unknown>) => {
      CanvasLogger.render.complete(cycleId, durationMs, data);
    },
    /** @deprecated Use CanvasLogger.render.skip() */
    skip: (reason: string, data?: Record<string, unknown>) => {
      CanvasLogger.render.skip(reason, data);
    },
    /** @deprecated Use CanvasLogger.render.throttle() */
    throttle: (timeSinceLastMs: number, data?: Record<string, unknown>) => {
      CanvasLogger.render.throttle(timeSinceLastMs, data);
    },
    /** @deprecated Use CanvasLogger.render.incremental() */
    incremental: (pageIndex: number, data?: Record<string, unknown>) => {
      CanvasLogger.render.incremental(pageIndex, data);
    },
  },

  pageSetup: {
    /** @deprecated Use CanvasLogger.page.setup.changed() */
    changed: (data?: Record<string, unknown>) => {
      CanvasLogger.page.setup.changed(data);
    },
    /** @deprecated Use CanvasLogger.page.setup.invalidate() */
    invalidate: (data?: Record<string, unknown>) => {
      CanvasLogger.page.setup.invalidate(data);
    },
    /** @deprecated Use CanvasLogger.page.setup.forceReinit() */
    forceReinit: (data?: Record<string, unknown>) => {
      CanvasLogger.page.setup.forceReinit(data);
    },
  },

  editCommand: {
    /** @deprecated Use CanvasLogger.edit.execute() */
    execute: (command: string, data?: Record<string, unknown>) => {
      CanvasLogger.edit.execute(command, data);
    },
    /** @deprecated Use CanvasLogger.edit.complete() */
    success: (command: string, data?: Record<string, unknown>) => {
      CanvasLogger.edit.complete(command, data);
    },
    /** @deprecated Use CanvasLogger.edit.fail() */
    failed: (command: string, error: string, data?: Record<string, unknown>) => {
      CanvasLogger.edit.fail(command, error, data);
    },
    /** @deprecated Use CanvasLogger.edit.gap.unavailable() */
    notAvailable: (data?: Record<string, unknown>) => {
      CanvasLogger.edit.gap.unavailable(data);
    },
  },

  domDiag: {
    /** @deprecated Use CanvasLogger.dom.diag() */
    pageContainer: (pageIndex: number, canvasCount: number, canvasSizes: string[], inputCount: number, editableCount: number, data?: Record<string, unknown>) => {
      CanvasLogger.dom.diag(pageIndex, canvasCount, canvasSizes, inputCount, editableCount, data);
    },
  },

  // Additional legacy aliases for direct method calls

  /** @deprecated Use CanvasLogger.pipeline.fail() */
  pipelineError: (operation: string, error: string, data?: Record<string, unknown>) => {
    CanvasLogger.pipeline.fail(operation, error, data);
  },

  /** @deprecated Use CanvasLogger.document.paragraphs.init() */
  paragraphsInit: (count: number, data?: Record<string, unknown>) => {
    CanvasLogger.document.paragraphs.init(count, data);
  },

  /** @deprecated Use CanvasLogger.document.transition() */
  emptyToNonEmpty: (data?: Record<string, unknown>) => {
    CanvasLogger.document.transition(data);
  },

  /** @deprecated Use CanvasLogger.layout.calculate.perf() */
  layoutCalculate: (durationMs: number, data?: Record<string, unknown>) => {
    CanvasLogger.layout.calculate.perf(durationMs, data);
  },
};
